// The Work page's API client (apps/server/src/work.mjs): projects, people and
// teams, work items, links, time, team members, files and photos.
import { ObjectsApiError } from '../objects/objectsApi';

export type Fetch = (input: string, init?: RequestInit) => Promise<Response>;

export type WorkStatus = 'backlog' | 'to_do' | 'in_progress' | 'in_review' | 'done';
export type WorkType = 'epic' | 'feature' | 'task' | 'bug' | 'subtask';
export type WorkPriority = 'highest' | 'high' | 'medium' | 'low' | 'lowest';
export type LinkType = 'blocks' | 'relates' | 'duplicates';
export type ProjectStatus = 'planned' | 'active' | 'on_hold' | 'done' | 'cancelled';

export interface WorkMe {
  id: string;
  access: 'all' | 'team';
  name: string;
}

export interface WorkProject {
  id: string;
  code: string;
  name: string;
  status: ProjectStatus;
  account_id: string;
  account_name: string;
  budget_hours: number | null;
  logged_hours: number;
}

export interface WorkPerson {
  id: string;
  name: string;
  email: string;
  project_access: 'all' | 'team';
  has_photo: boolean;
}

export interface WorkBootstrap {
  me: WorkMe | null;
  projects: WorkProject[];
  people: WorkPerson[];
  members: { project_id: string; app_user_id: string }[];
  /** Contacts at the projects' accounts, to assign work to; empty for a team-only person. */
  contacts: WorkContact[];
  labels: string[];
}

export interface WorkContact {
  id: string;
  name: string;
  account_id: string;
}

export interface WorkItem {
  id: string;
  project_id: string;
  key: string;
  number: number;
  type: WorkType;
  title: string;
  description: string | null;
  status: WorkStatus;
  is_blocked: boolean;
  priority: WorkPriority;
  assignee_id: string | null;
  assignee: { id: string; name: string; has_photo: boolean } | null;
  /** A contact at the client, assigned instead of a team member. */
  assignee_contact_id: string | null;
  assignee_contact: { id: string; name: string } | null;
  parent_id: string | null;
  parent_key: string | null;
  parent_title: string | null;
  parent_type: WorkType | null;
  epic_id: string | null;
  epic_title: string | null;
  due_date: string | null;
  board_rank: number;
  created_by_id: string | null;
  created_at: string;
  updated_at: string;
  closed_at: string | null;
  archived_at: string | null;
  labels: string[];
  logged_hours: number;
  /** Keys of open items that block this one. */
  blocked_by: string[];
}

export interface WorkLink {
  id: string;
  link_type: LinkType;
  outgoing: boolean;
  other_id: string;
  other_key: string;
  other_title: string;
  other_type: WorkType;
  other_status: WorkStatus;
}

export interface TimeEntry {
  id: string;
  project_id: string;
  work_item_id: string | null;
  entry_date: string;
  hours: number;
  billable: boolean;
  notes: string | null;
  owner_id: string;
  created_by_id: string | null;
  account_id: string;
}

export interface WorkFile {
  id: string;
  name: string;
  mime_type: string;
  byte_size: number;
  uploaded_at: string;
  owner_id: string | null;
  entity_type: 'project' | 'work_item';
  entity_id: string;
  /** False for a file listed without its contents (left behind by the Supabase copy). */
  stored: boolean;
  /** The work item's key and title, on a project's list of files. */
  item_key: string | null;
  item_title: string | null;
}

export interface WorkItemDetail {
  item: WorkItem;
  children: WorkItem[];
  links: WorkLink[];
  time: TimeEntry[];
  files: WorkFile[];
}

export type WorkCommentAuthor =
  | { type: 'person'; id: string; name: string }
  | { type: 'agent'; role: 'support' | 'pm' | 'engineer' | 'reviewer' | 'qa'; name: string; run: string | null };

export interface WorkComment {
  id: string;
  kind: 'comment' | 'event';
  body: string;
  created_at: string;
  edited_at: string | null;
  author: WorkCommentAuthor | null;
  mine: boolean;
}

export interface TeamMember {
  app_user_id: string;
  name: string;
  email: string;
  project_access: 'all' | 'team';
  has_photo: boolean;
  source: 'assignment' | 'manual';
  added_at: string;
  removed_at: string | null;
  role: string | null;
  assigned_hours: number;
  first_month: string | null;
  last_month: string | null;
}

export interface NewWorkItem {
  project_id: string;
  title: string;
  type?: WorkType;
  status?: WorkStatus;
  parent_id?: string | null;
  assignee_id?: string | null;
  assignee_contact_id?: string | null;
  priority?: WorkPriority;
  due_date?: string | null;
  labels?: string[];
  description?: string | null;
}

export type WorkItemPatch = Partial<{
  title: string;
  description: string | null;
  type: WorkType;
  status: WorkStatus;
  is_blocked: boolean;
  assignee_id: string | null;
  assignee_contact_id: string | null;
  priority: WorkPriority;
  parent_id: string | null;
  due_date: string | null;
  labels: string[];
  archived: boolean;
  position: { before_id: string | null; after_id: string | null };
}>;

export interface TimeInput {
  entry_date: string;
  hours: number;
  project_id: string;
  work_item_id: string | null;
  owner_id: string;
  billable: boolean;
  notes: string | null;
}

export interface TimesheetCell {
  project_id: string;
  work_item_id: string | null;
  date: string;
  hours: number;
}

async function errorOf(response: Response, fallback: string): Promise<ObjectsApiError> {
  let message = fallback;
  try {
    const body = (await response.json()) as { error?: unknown };
    if (typeof body.error === 'string' && body.error !== '') message = body.error;
  } catch {
    // Not JSON: the fallback says enough.
  }
  return new ObjectsApiError(response.status, message);
}

async function json<T>(fetcher: Fetch, path: string, fallback: string, init?: { method: string; body?: unknown }): Promise<T> {
  const response = await fetcher(path, init
    ? {
        method: init.method,
        ...(init.body === undefined ? {} : { headers: { 'content-type': 'application/json' }, body: JSON.stringify(init.body) }),
      }
    : undefined);
  if (!response.ok) throw await errorOf(response, fallback);
  return (await response.json()) as T;
}

const id = (value: string) => encodeURIComponent(value);

export interface RelatedOpportunity {
  id: string;
  name: string;
  stage: string;
  amount: number | null;
  close_date: string | null;
  account_id: string | null;
  account_name: string | null;
}

export interface RelatedMeeting {
  id: string;
  meeting_date: string | null;
  /** The title, else "<Category> Meeting · <date>". */
  name: string;
  meeting_category: string | null;
  summary_line: string | null;
}

/** Hours on the project in a month; a null person is forecast hours no one fills yet. */
export interface MonthHours {
  month: string;
  app_user_id: string | null;
  name: string | null;
  hours: number;
}

/** Null opportunities and meetings: the caller sees only their teams' projects. */
export interface ProjectRelated {
  opportunities: RelatedOpportunity[] | null;
  meetings: RelatedMeeting[] | null;
  assignments: MonthHours[];
}

export interface LoggedMonth {
  month: string;
  owner_id: string;
  hours: number;
  billable_hours: number;
}

export const fetchRelated = (f: Fetch, projectId: string) =>
  json<ProjectRelated>(f, `/api/work/projects/${id(projectId)}/related`, 'Could not load the related records.');

export async function fetchTimeMonths(f: Fetch, q: { project?: string; mine?: boolean }) {
  const query = q.project ? `?project=${id(q.project)}` : q.mine ? '?mine=1' : '';
  return (await json<{ months: LoggedMonth[] }>(f, `/api/work/time/months${query}`, 'Could not load the hours.')).months;
}

export const fetchBootstrap = (f: Fetch) => json<WorkBootstrap>(f, '/api/work/bootstrap', 'Could not load your projects.');

export async function fetchItems(f: Fetch, scope: { project: string } | { mine: true } | { all: true }): Promise<WorkItem[]> {
  const query = 'project' in scope ? `?project=${id(scope.project)}` : 'mine' in scope ? '?mine=1' : '';
  return (await json<{ items: WorkItem[] }>(f, `/api/work/items${query}`, 'Could not load the work items.')).items;
}

export const fetchItemDetail = (f: Fetch, itemId: string) =>
  json<WorkItemDetail>(f, `/api/work/items/${id(itemId)}`, 'Could not load the work item.');

export const fetchWorkItemComments = (f: Fetch, itemId: string) =>
  json<{ comments: WorkComment[] }>(f, `/api/work/items/${id(itemId)}/comments`, 'Could not load the comments.');

export const createWorkComment = (f: Fetch, itemId: string, body: string) =>
  json<WorkComment>(f, `/api/work/items/${id(itemId)}/comments`, 'Could not post the comment.', { method: 'POST', body: { body } });

export const updateWorkComment = (f: Fetch, commentId: string, body: string) =>
  json<WorkComment>(f, `/api/work/comments/${id(commentId)}`, 'Could not save the comment.', { method: 'PATCH', body: { body } });

export async function deleteWorkComment(f: Fetch, commentId: string): Promise<void> {
  const response = await f(`/api/work/comments/${id(commentId)}`, { method: 'DELETE' });
  if (!response.ok) throw await errorOf(response, 'Could not delete the comment.');
}

export const createItem = (f: Fetch, body: NewWorkItem) =>
  json<WorkItem>(f, '/api/work/items', 'Could not create the work item.', { method: 'POST', body });

export const updateItem = (f: Fetch, itemId: string, body: WorkItemPatch) =>
  json<WorkItem>(f, `/api/work/items/${id(itemId)}`, 'Could not save the change.', { method: 'PATCH', body });

export const deleteItem = (f: Fetch, itemId: string) =>
  json<{ deleted: number }>(f, `/api/work/items/${id(itemId)}`, 'Could not delete the work item.', { method: 'DELETE' });

export const archiveItems = (f: Fetch, ids: string[]) =>
  json<{ archived: number }>(f, '/api/work/items/archive', 'Could not archive them.', { method: 'POST', body: { ids } });

export const createLink = (f: Fetch, body: { from_item_id: string; to_item_id: string; link_type: LinkType }) =>
  json<{ id: string }>(f, '/api/work/links', 'Could not link them.', { method: 'POST', body });

export const deleteLink = (f: Fetch, linkId: string) =>
  json<{ ok: true }>(f, `/api/work/links/${id(linkId)}`, 'Could not remove the link.', { method: 'DELETE' });

export async function fetchTime(f: Fetch, owner: string, from: string, to: string, project?: string): Promise<TimeEntry[]> {
  const query = `?owner=${id(owner)}&from=${from}&to=${to}${project ? `&project=${id(project)}` : ''}`;
  return (await json<{ entries: TimeEntry[] }>(f, `/api/work/time${query}`, 'Could not load the time.')).entries;
}

export const fetchTimePeriod = (f: Fetch, q: { owner: string; month: string; project?: string | undefined; exclude?: string | undefined }) =>
  json<{ owner_hours: number; project_hours: number }>(
    f,
    `/api/work/time/period?owner=${id(q.owner)}&month=${q.month}${q.project ? `&project=${id(q.project)}` : ''}${q.exclude ? `&exclude=${id(q.exclude)}` : ''}`,
    'Could not load the month.',
  );

export const createTime = (f: Fetch, body: TimeInput) =>
  json<TimeEntry>(f, '/api/work/time', 'Could not log the time.', { method: 'POST', body });

export const updateTime = (f: Fetch, entryId: string, body: Partial<TimeInput>) =>
  json<TimeEntry>(f, `/api/work/time/${id(entryId)}`, 'Could not save the time entry.', { method: 'PATCH', body });

export const deleteTime = (f: Fetch, entryId: string) =>
  json<{ deleted: number }>(f, `/api/work/time/${id(entryId)}`, 'Could not delete the time entry.', { method: 'DELETE' });

export const saveTimesheet = (
  f: Fetch,
  body: { owner_id: string; cells: TimesheetCell[]; billable?: { project_id: string; work_item_id: string | null; from: string; to: string; billable: boolean }[] },
) => json<{ changed: number }>(f, '/api/work/timesheet', 'Could not save the timesheet.', { method: 'PUT', body });

export async function fetchTeam(f: Fetch, projectId: string): Promise<TeamMember[]> {
  return (await json<{ members: TeamMember[] }>(f, `/api/work/projects/${id(projectId)}/team`, 'Could not load the team.')).members;
}

export const addMember = (f: Fetch, projectId: string, appUserId: string) =>
  json<{ ok: true }>(f, `/api/work/projects/${id(projectId)}/team`, 'Could not add them.', { method: 'POST', body: { app_user_id: appUserId } });

export const removeMember = (f: Fetch, projectId: string, appUserId: string) =>
  json<{ ok: true }>(f, `/api/work/projects/${id(projectId)}/team/${id(appUserId)}`, 'Could not remove them.', { method: 'DELETE' });

export async function fetchProjectFiles(f: Fetch, projectId: string): Promise<WorkFile[]> {
  return (await json<{ files: WorkFile[] }>(f, `/api/work/projects/${id(projectId)}/files`, 'Could not load the files.')).files;
}

/** The content types the server accepts, by extension, for files a browser leaves untyped. */
const TYPE_BY_EXTENSION: Record<string, string> = {
  pdf: 'application/pdf',
  png: 'image/png',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  csv: 'text/csv',
  txt: 'text/plain',
  docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  pptx: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
};

export function contentTypeOf(file: { name: string; type: string }): string | null {
  const known = Object.values(TYPE_BY_EXTENSION);
  if (known.includes(file.type)) return file.type;
  return TYPE_BY_EXTENSION[file.name.split('.').pop()?.toLowerCase() ?? ''] ?? null;
}

export async function uploadFile(f: Fetch, entity: 'project' | 'work_item', targetId: string, file: File): Promise<WorkFile> {
  const type = contentTypeOf(file);
  if (!type) throw new ObjectsApiError(400, `${file.name}: attach a PDF, an image, a Word, Excel or PowerPoint file, CSV or text.`);
  const response = await f(`/api/work/files?entity=${entity}&id=${id(targetId)}`, {
    method: 'POST',
    headers: { 'content-type': type, 'x-file-name': encodeURIComponent(file.name) },
    body: file,
  });
  if (!response.ok) throw await errorOf(response, `Could not attach ${file.name}.`);
  return (await response.json()) as WorkFile;
}

export const removeFile = (f: Fetch, fileId: string, entity: 'project' | 'work_item', targetId: string) =>
  json<{ ok: true }>(f, `/api/work/files/${id(fileId)}?entity=${entity}&id=${id(targetId)}`, 'Could not remove the file.', { method: 'DELETE' });

/** Opens a stored file in a new tab (the API needs the session, so it is fetched first). */
export async function openFile(f: Fetch, file: Pick<WorkFile, 'id' | 'name'>): Promise<void> {
  const tab = window.open('', '_blank');
  const response = await f(`/api/work/files/${id(file.id)}`);
  if (!response.ok) {
    tab?.close();
    throw await errorOf(response, `Could not open ${file.name}.`);
  }
  const url = URL.createObjectURL(await response.blob());
  if (tab) tab.location.href = url;
  else window.location.assign(url);
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

/** A stored file as an object URL (a research figure's image), or null when it cannot be read. */
export async function fetchFileUrl(f: Fetch, fileId: string): Promise<string | null> {
  const response = await f(`/api/work/files/${id(fileId)}`);
  if (!response.ok) return null;
  return URL.createObjectURL(await response.blob());
}

/** A person's photo as an object URL, or null when they have none. */
export async function fetchPhoto(f: Fetch, personId: string): Promise<string | null> {
  const response = await f(`/api/people/${id(personId)}/photo`);
  if (!response.ok) return null;
  return URL.createObjectURL(await response.blob());
}

// ── The top bar's timer (apps/server/src/timer.mjs) ─────────────────────────

export interface TimerPunchRow {
  id: string;
  project_id: string;
  project_code: string;
  project_name: string;
  started_at: string;
  ended_at: string | null;
  reconciled_at: string | null;
}

export interface TimerData {
  punches: TimerPunchRow[];
  projects: { id: string; code: string; name: string; account_name: string }[];
}

/** My punches overlapping [from, to) (zoned ISO times) and the projects I can time. */
export function fetchTimer(f: Fetch, from: string, to: string): Promise<TimerData> {
  return json(f, `/api/work/timer?from=${id(from)}&to=${id(to)}`, 'Unable to load your time.');
}

export function startTimer(f: Fetch, projectId: string): Promise<unknown> {
  return json(f, '/api/work/timer/start', 'Unable to start the timer.', { method: 'POST', body: { project_id: projectId } });
}

export function stopTimer(f: Fetch): Promise<unknown> {
  return json(f, '/api/work/timer/stop', 'Unable to stop the timer.', { method: 'POST', body: {} });
}

export function updatePunch(f: Fetch, punchId: string, times: { started_at: string; ended_at: string | null }): Promise<TimerPunchRow> {
  return json(f, `/api/work/timer/punches/${id(punchId)}`, 'Unable to save.', { method: 'PATCH', body: times });
}

export function deletePunch(f: Fetch, punchId: string): Promise<unknown> {
  return json(f, `/api/work/timer/punches/${id(punchId)}`, 'Unable to delete.', { method: 'DELETE' });
}

// ── Client feedback (apps/server/src/clientFeedback.mjs, MOTHER-325 and MOTHER-327): Admins only ──

export type ClientFeedbackStatus = 'new' | 'triaged' | 'dismissed';

/** One message a client app's user sent about a project, as GET /api/client-feedback answers it. */
export interface ClientFeedback {
  id: string;
  source: string;
  submitterName: string | null;
  submitterEmail: string | null;
  submitterRole: string | null;
  body: string;
  pageUrl: string | null;
  status: ClientFeedbackStatus;
  /** The work item it was turned into, e.g. ACME-12; null until it is. */
  workItemKey: string | null;
  workItemId: string | null;
  receivedAt: string;
}

export async function fetchClientFeedback(f: Fetch, projectId: string, status: ClientFeedbackStatus): Promise<ClientFeedback[]> {
  const path = `/api/client-feedback?project=${id(projectId)}&status=${id(status)}`;
  return (await json<{ items: ClientFeedback[] }>(f, path, 'Could not load the feedback.')).items;
}

/** Turns a new row into a backlog task on its project; 409 (ObjectsApiError) once it is no longer new. */
export const convertClientFeedback = (f: Fetch, feedbackId: string) =>
  json<{ workItemKey: string }>(f, `/api/client-feedback/${id(feedbackId)}/convert`, 'Could not convert the feedback.', { method: 'POST', body: {} });

/** Dismisses a new row; 409 (ObjectsApiError) once it is no longer new. */
export const dismissClientFeedback = (f: Fetch, feedbackId: string) =>
  json<{ status: 'dismissed' }>(f, `/api/client-feedback/${id(feedbackId)}/dismiss`, 'Could not dismiss the feedback.', { method: 'POST' });
